// ----------------------------------------------------------------------
// <copyright file="Izenda.Izenda.SchedulingSearchCriteria.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Scheduling Search Criteria
 */
!(function() {
  var extendObservable = require('mobx').extendObservable;
  var action = require('mobx').action;
  var CONSTANTS = require('logic/data/Izenda.SchedulingSearchConstants');
  var LAST_SUCCESS_RUN_VALUE = CONSTANTS.LAST_SUCCESS_RUN_VALUE;
  var DELIVERY_TYPE_VALUE = CONSTANTS.DELIVERY_TYPE_VALUE;
  var DELIVER_METHOD_VALUE = CONSTANTS.DELIVER_METHOD_VALUE;
  var DELIVERY_METHOD = CONSTANTS.DELIVERY_METHOD;
  var RECURRENCE = CONSTANTS.RECURRENCE;
  var INSTANCE_TYPE_VALUE = CONSTANTS.INSTANCE_TYPE_VALUE;
  var RECURRENCE_VALUE = CONSTANTS.RECURRENCE_VALUE;
  var DEFAULT_OPTION = CONSTANTS.DEFAULT_OPTION;
  const { compose, concat, clone, map, split, cloneDeep } = require('lodash/fp');

  var buildOptionFromData = compose(
    concat(DEFAULT_OPTION),
    map(function(item) {
      return {
        value: item,
        label: item
      };
    })
  );

  function SchedulingSearchCriteria() {
    var originCriteria = {
      reportType: '',
      name: '',
      instanceType: '',
      recurrence: '',
      createdBy: '',
      deliveryType: '',
      deliveryMethod: '',
      exportFileType: '',
      recipients: '',
      lastSuccessfulRun: '',
      lastSuccessfulRunFrom: '',
      lastSuccessfulRunTo: '',
      nextScheduledRun: '',
      nextScheduledRunFrom: '',
      nextScheduledRunTo: '',
      reportDashboardNameOptions: [],
      createdByOptions: [DEFAULT_OPTION],
      recipientsOptions: [DEFAULT_OPTION]
    };
    this.originCriteria = originCriteria;

    extendObservable(this, clone(originCriteria));
    extendObservable(this, {
      isDateLastSuccessRun: function() {
        return this.lastSuccessfulRun === LAST_SUCCESS_RUN_VALUE.DATE;
      },
      isNextScheduledRun: function() {
        return this.nextScheduledRun === LAST_SUCCESS_RUN_VALUE.DATE;
      },
      isHiddenRecipients: function() {
        return (
          this.instanceType === INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM ||
          this.instanceType === INSTANCE_TYPE_VALUE.SUBSCRIBE_ALERT
        );
      },
      deliveryMethodOptions: function() {
        var deliveryType = this.deliveryType;
        if (!deliveryType) {
          return DELIVERY_METHOD;
        }
        if (
          deliveryType.indexOf(DELIVERY_TYPE_VALUE.EMAIL) > -1 &&
          deliveryType.indexOf(DELIVERY_TYPE_VALUE.FILE_LOCATION) > -1
        ) {
          return DELIVERY_METHOD;
        } else if (deliveryType.indexOf(DELIVERY_TYPE_VALUE.EMAIL) > -1) {
          return DELIVERY_METHOD.filter(function(item) {
            return item.value !== DELIVER_METHOD_VALUE.SEND_TO_DISK;
          });
        } else if (deliveryType.indexOf(DELIVERY_TYPE_VALUE.FILE_LOCATION) > -1) {
          return DELIVERY_METHOD.filter(function(item) {
            return item.value === DELIVER_METHOD_VALUE.SEND_TO_DISK || item.value === DELIVER_METHOD_VALUE.EMPTY;
          });
        }
      },
      recurrenceOptions: function() {
        var instanceType = this.instanceType;
        if (!instanceType) {
          return RECURRENCE;
        }
        if (
          instanceType.indexOf(INSTANCE_TYPE_VALUE.SUBSCRIBE_ALERT) === -1 &&
          instanceType.indexOf(INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM) === -1
        ) {
          return RECURRENCE.filter(function(item) {
            return item.value !== RECURRENCE_VALUE.EVERY_HOURLY || item.value !== RECURRENCE_VALUE.EVERY_DAILY;
          });
        } else if (
          instanceType.indexOf(INSTANCE_TYPE_VALUE.SCHEDULE_ALERT) === -1 &&
          instanceType.indexOf(INSTANCE_TYPE_VALUE.SCHEDULE_REPORT) === -1
        ) {
          return RECURRENCE.filter(function(item) {
            return (
              item.value === RECURRENCE_VALUE.EVERY_HOURLY ||
              item.value === RECURRENCE_VALUE.EVERY_DAILY ||
              item.value === RECURRENCE_VALUE.EMPTY ||
              item.value === RECURRENCE_VALUE.CUSTOM
            );
          });
        }
      }
    });
  }

  /**
   * Set Criteria value by it's key
   * @param {string} key
   * @param {string} value
   */
  SchedulingSearchCriteria.prototype.setValueByKey = function(key, value) {
    this[key] = value;
  };

  /**
   * Set Criteria Option by key
   * @param {string} key
   * @param {array} data
   */
  SchedulingSearchCriteria.prototype.setOptionByKey = function(key, data) {
    this[key] = buildOptionFromData(data);
  };

  /**
   * Get Criteria for multi value
   * @param {string} key
   * @returns {Array}
   */
  SchedulingSearchCriteria.prototype.getMultiValueCriteria = function(key) {
    return split(';', this[key]);
  };

  /**
   * Reset All Criteria to origin
   */
  SchedulingSearchCriteria.prototype.resetData = action(function() {
    var keys = Object.keys(this.originCriteria);
    keys.forEach(
      function(key) {
        this[key] = this.originCriteria[key];
      }.bind(this)
    );
  });

  /**
   * Build Data for Search
   * @returns {object}
   */
  SchedulingSearchCriteria.prototype.buildDataForSearch = function() {
    var result = cloneDeep(this);
    if (!this.isDateLastSuccessRun) {
      result.lastSuccessfulRunFrom = null;
      result.lastSuccessfulRunTo = null;
    }
    if (!this.isNextScheduledRun) {
      result.nextScheduledRunFrom = null;
      result.nextScheduledRunTo = null;
    }
    return result;
  };

  module.exports = SchedulingSearchCriteria;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SchedulingSearchCriteria.js